/*
 * FROSTBEULE · Flocken & Einfrieren (owner: DEMO) – <frost-freeze> in sections/frost-freeze.liquid.
 * Steps get .is-active one after another while the visitor scrolls (IntersectionObserver, no scroll-jacking,
 * no sticky panel); skipped steps are caught up in order with a short stagger, so every scene still plays.
 * The thermometer rail is laid out from the marker positions; its mercury sinks to the next marker with every
 * reached step and turns icy, the bulb freezes at the last one. Motion: full = scenes animate (.is-full),
 * soft = fades only, none = everything shown in its final state. Snow: declarative [data-fb-snow] in the
 * section, mounted by window.FBSnow (frost-global.js).
 * Theme editor: selecting a step block plays everything up to that step.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('frost-freeze')) return;

  var doc = document;
  var root = doc.documentElement;

  function motion() {
    var F = window.FROSTBEULE;
    if (F && typeof F.motion === 'function') return F.motion();
    return root.getAttribute('data-motion') || 'full';
  }

  class FrostFreeze extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this._alive = true;
      this.steps = Array.prototype.slice.call(this.querySelectorAll('[data-fz-step]'));
      this.thermo = this.querySelector('[data-fz-thermo]');
      this.active = -1;
      this.queue = [];
      this.timer = null;

      this.layout();

      if (motion() !== 'none' && 'IntersectionObserver' in window && this.steps.length) this.arm();
      else this.showAll();

      var self = this;
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(function () {
          self.layout();
        });
        this.ro.observe(this);
      } else {
        this._onResize = function () {
          self.layout();
        };
        window.addEventListener('resize', this._onResize);
      }

      this._onMotion = function (e) {
        var m = e && e.detail ? e.detail.motion : motion();
        if (m === 'none') self.showAll();
        else self.classList.toggle('is-full', m === 'full' && self.classList.contains('is-armed'));
      };
      doc.addEventListener('frost:motion-change', this._onMotion);

      this._onBlock = function (e) {
        var step = e.target && e.target.closest ? e.target.closest('[data-fz-step]') : null;
        if (!step || !self.contains(step)) return;
        self.activateUpTo(self.steps.indexOf(step));
      };
      doc.addEventListener('shopify:block:select', this._onBlock);
    }

    disconnectedCallback() {
      this._alive = false;
      this._ready = false;
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this.timer) clearTimeout(this.timer);
      doc.removeEventListener('frost:motion-change', this._onMotion);
      doc.removeEventListener('shopify:block:select', this._onBlock);
    }

    /* ------------------------------------------------------------ steps */
    arm() {
      var self = this;
      this.classList.add('is-arming', 'is-armed');
      this.classList.toggle('is-full', motion() === 'full');
      this.setLevel(-1);
      void this.offsetWidth; /* commit the „before“ poses without transitions */
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          self.classList.remove('is-arming');
        });
      });
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.activateUpTo(self.steps.indexOf(entry.target));
          });
        },
        { rootMargin: '0px 0px -15% 0px', threshold: 0 }
      );
      this.steps.forEach(function (s) {
        self.io.observe(s);
      });
    }

    activateUpTo(index) {
      if (index < 0) return;
      var last = this.queue.length ? this.queue[this.queue.length - 1] : this.active;
      for (var k = last + 1; k <= index; k++) this.queue.push(k);
      this.pump();
    }

    pump() {
      if (this.timer || !this.queue.length) return;
      var k = this.queue.shift();
      this.activate(k);
      var self = this;
      this.timer = setTimeout(function () {
        self.timer = null;
        self.pump();
      }, this.queue.length ? 320 : 0);
    }

    activate(k) {
      var step = this.steps[k];
      if (!step) return;
      step.classList.add('is-active');
      if (this.io) this.io.unobserve(step);
      if (k > this.active) this.active = k;
      this.setLevel(this.active);
      if (this.active >= this.steps.length - 1 && this.io) {
        this.io.disconnect();
        this.io = null;
      }
    }

    showAll() {
      if (this.io) this.io.disconnect();
      this.io = null;
      this.queue = [];
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
      this.classList.remove('is-armed', 'is-full', 'is-arming');
      this.steps.forEach(function (s) {
        s.classList.add('is-active');
      });
      this.active = this.steps.length - 1;
      this.setLevel(this.active);
    }

    /* ------------------------------------------------------------ thermometer */
    layout() {
      var th = this.thermo;
      if (!th || !this.steps.length) return;
      var host = this.getBoundingClientRect();
      var markers = this.steps.map(function (s) {
        var m = s.querySelector('[data-fz-marker]');
        if (!m) return null;
        var r = m.getBoundingClientRect();
        return r.top - host.top + r.height / 2;
      });
      if (markers.some(function (v) {
        return v == null;
      }))
        return;
      this.markers = markers;
      var top = markers[0] - 10;
      var bulbCenter = markers[markers.length - 1] + 84;
      th.style.setProperty('--fz-top', top + 'px');
      th.style.setProperty('--fz-h', Math.max(80, bulbCenter + 21 - top) + 'px');
      this.geo = { top: top, bottom: bulbCenter - 16 };
      this.setLevel(this.active);
    }

    /* level after step k: mercury top sits at the next marker (or drains into the bulb after the last) */
    setLevel(k) {
      var th = this.thermo;
      if (!th) return;
      var armed = this.classList.contains('is-armed');
      var n = this.steps.length;
      var level = 0.02;
      if (this.geo && this.markers) {
        var span = Math.max(1, this.geo.bottom - this.geo.top);
        var target = k + 1 < n ? this.markers[k + 1] : this.geo.bottom;
        if (k < 0) target = this.markers[0];
        level = Math.min(1, Math.max(0.02, (this.geo.bottom - target) / span));
      } else if (armed) {
        level = k < 0 ? 1 : Math.max(0.02, 1 - (k + 1) / n);
      }
      if (!armed && k >= n - 1) level = 0.02;
      var cold = n > 0 ? Math.min(1, Math.max(0, (k + 1) / n)) : 1;
      th.style.setProperty('--fz-level', level.toFixed(4));
      th.style.setProperty('--fz-cold', cold.toFixed(3));
      this.classList.toggle('is-cold', k >= n - 1);
    }
  }

  window.customElements.define('frost-freeze', FrostFreeze);
})();
